import { Injectable, ChangeDetectorRef } from '@angular/core';
import { UtilService } from './util.service';
import { SharedDataService } from './shared-data.service';
import { ActivityService } from './activity.service';
import { EnumService } from './enum.service';

@Injectable({
  providedIn: 'root'
})
export class ActivityPopupHandlerService {
  showActivityPopup = false;
  showActivitySummary = false;
  showSignoffError = false;
  activityPopupData: any;
  openedFromSummary = false;

  EnumService = EnumService;

  constructor(
    private sharedDataService: SharedDataService,
    private activityService: ActivityService,
  ) {}

  public areAllFieldsPredefined(logic: any): boolean {
    if (!logic) return false;
    
    const fields = [
      'isDatePredefined',
      'isActTitlePredefined',
      'isAssigneePredefined',
      'isDescriptionPredefined',
      'isExplanationPredefined',
      'isPriorityPredefined',
      'isOwnerPredefined'
    ];
    
    return fields.every(field => logic[field] === true || logic[field] === null);
  }

  // A logic line is evaluated against the question's whole answer state (every selected answer),
  // honouring its Selected / Not Selected operator. An unanswered question never triggers it.
  public isActivityLogicMet(logic: any, selectedSequences: number[]): boolean {
    if (!selectedSequences.length) return false;
    const isTriggerSelected = selectedSequences.includes(logic.questionChoiceSetSequence);
    return this.isNotSelectedLogic(logic) ? !isTriggerSelected : isTriggerSelected;
  }

  // Index of the selected answer that satisfies the logic, preferring the one just clicked.
  public getTriggeringAnswerIndex(logic: any, selectedSequences: number[], preferredSequence?: number): number {
    const satisfies = (sequence: number) =>
      this.isNotSelectedLogic(logic)
        ? sequence !== logic.questionChoiceSetSequence
        : sequence === logic.questionChoiceSetSequence;

    if (preferredSequence && selectedSequences.includes(preferredSequence) && satisfies(preferredSequence)) {
      return preferredSequence - 1;
    }
    const sequence = selectedSequences.find(satisfies);
    return sequence ? sequence - 1 : -1;
  }

  // Answers the popup's "Deselect" clears so the logic is no longer met: the triggering answer for
  // Selected, every answer except the trigger choice for Not Selected.
  public getDeselectRefs(logic: any, question: any, answerRef: any): any[] {
    if (!this.isNotSelectedLogic(logic)) return [answerRef];
    return (question.answerChoiceAttributes || [])
      .filter((a: any, index: number) => index + 1 !== logic.questionChoiceSetSequence)
      .map((a: any) => a.answerChoiceAttributeId);
  }

  private isNotSelectedLogic(logic: any): boolean {
    return logic.questionChoiceSetTypeID === EnumService.QuestionLogic.SingleMultipleChoiceOperationType.NotSelected;
  }

  // Single / multiple choice: re-evaluates every activity logic of the question on each click.
  // Drops the activity of a logic that is no longer met, keeps the one of a logic that stays met,
  // and returns a newly met logic (with its triggering answer) to prompt for.
  private syncChoiceSetActivities(event: any, question: any, clickedSequence: number) {
    const answers = question.answerChoiceAttributes || [];
    const clickedId = event.answer.answerChoiceAttributeId;
    const selectedIds: number[] =
      event.selectedAnswerIds ??
      (question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.SingleChoiceSet || event.answer.isChecked
        ? [clickedId]
        : []);
    const selectedSequences = answers
      .map((a: any, index: number) => (selectedIds.includes(a.answerChoiceAttributeId) ? index + 1 : 0))
      .filter((sequence: number) => sequence > 0);

    let relevantQuestionLogic = null;
    (question.questionLogics || [])
      .filter((logic: any) => logic.questionActionTypeID === 20 && logic.questionID === question.questionId)
      .forEach((logic: any) => {
        // Fully predefined activities need no user input, so they never get a popup / summary item
        if (this.areAllFieldsPredefined(logic)) return;

        const existingItem = this.activityService.findSummaryItemForLogic(
          event.section.sectionId, question.questionId, logic.questionLogicId
        );
        if (!this.isActivityLogicMet(logic, selectedSequences)) {
          if (existingItem) {
            this.activityService.removeSummaryItemForLogic(event.section.sectionId, question.questionId, logic.questionLogicId);
          }
        } else if (!existingItem && !relevantQuestionLogic) {
          relevantQuestionLogic = logic;
        }
      });

    const answerIndex = relevantQuestionLogic
      ? this.getTriggeringAnswerIndex(relevantQuestionLogic, selectedSequences, clickedSequence)
      : clickedSequence - 1;
    return { relevantQuestionLogic, answerIndex };
  }

  checkForNewActivity(event: any, formBuilderDetail: any, cdRef: ChangeDetectorRef) {
    let questionText = '';
    let answerText = '';
    let relevantQuestionLogic = null;

    const section = formBuilderDetail.sections.find(
      (s: any) => s.sectionId === event.section.sectionId
    );
    if (!section) return;

    const question = section.questions.find(
      (q: any) => q.questionId === event.questionId
    );
    if (!question) return;

    // Get question text
    questionText = UtilService.findObj(
      question.questionTranslations,
      'questionTranslationLanguageId',
      this.sharedDataService.getLanguageIdForForm()
    ).questionTranslationTitle;

    const isConfirmationBox = question.selectedAnswerTypeId === this.EnumService.CustomAnswerType.ConfirmationBox;
  
    let answer: any;
    let answerIndex: number;
    
    // Get answer object
    if (!isConfirmationBox) {
      answerIndex = question.answerChoiceAttributes.findIndex(
        (a: any) => a.answerChoiceAttributeId === event.answer.answerChoiceAttributeId
      );
      answer = question.answerChoiceAttributes[answerIndex];
      answerText = answer?.answerChoiceAttributeHeaders[0]?.answerChoiceAttributeHeaderTitle || '';
    } else {
      // For confirmation box
      answer = { isSelected: event.answer.isChecked !== false };
      answerText = 'Confirmed';
    }

    // Get answer object
    if (!isConfirmationBox) {
      ({ relevantQuestionLogic, answerIndex } = this.syncChoiceSetActivities(event, question, answerIndex + 1));
      answer = question.answerChoiceAttributes[answerIndex];
      answerText = answer?.answerChoiceAttributeHeaders[0]?.answerChoiceAttributeHeaderTitle || '';
    } else {
      relevantQuestionLogic = question.questionLogics?.find(
        (logic: any) => logic.questionActionTypeID === 20
      );
    }


    if (this.areAllFieldsPredefined(relevantQuestionLogic)) {
      console.log('All fields are predefined, skipping popup');
      return;
    }

    if (isConfirmationBox && !this.sharedDataService.isRestoring && !answer?.isSelected) {
      const sectionTitle = UtilService.findObj(
        event.section.sectionTranslations,
        'sectionTranslationLanguageId',
        this.sharedDataService.getLanguageIdForForm()
      ).sectionTranslationTitle;

      const existingItem = this.activityService.getSummaryItems().find(
        (it: any) =>
          it.section === sectionTitle &&
          it.question === questionText &&
          it.answer === answerText &&
          it.sectionId === event.section.sectionId &&
          it.questionId === event.questionId &&
          it.answer === answerText
      );
      console.log('existingItem', existingItem);
      if (existingItem && existingItem.isSelected) {
        console.log('Removing summary item:', existingItem);
        this.activityService.removeSummaryItemByData(
          sectionTitle,
          questionText,
          answerText,
          event.section.sectionId,
          question.questionId
        );
        relevantQuestionLogic = null;
        this.showActivityPopup = false;
        return;
      }
    }

    const ENTITY_OWNER_GUID = '6f545e05-72dc-471c-a352-95728c6cd261'
    const enhancedUserList = [
      ...(formBuilderDetail.userList || []),
      {
        userId: ENTITY_OWNER_GUID,
        userName: '= ENTITY OWNER'
      }
    ];

    this.activityPopupData = {
      sectionName: UtilService.findObj(
        event.section.sectionTranslations,
        'sectionTranslationLanguageId',
        this.sharedDataService.getLanguageIdForForm()
      ).sectionTranslationTitle,
      questionText: questionText,
      answerText: answerText,
      questionLogic: relevantQuestionLogic,
      userList: enhancedUserList,
      companyEntityRoles: formBuilderDetail.companyEntityRoles,
      sectionId: event.section.sectionId,
      questionId: event.questionId,
      answerRef:
        question.selectedAnswerTypeId ===
        this.EnumService.CustomAnswerType.ConfirmationBox
          ? event.answer.questionId
          : answer?.answerChoiceAttributeId,
      deselectRefs: relevantQuestionLogic && !isConfirmationBox
        ? this.getDeselectRefs(relevantQuestionLogic, question, answer?.answerChoiceAttributeId)
        : undefined
    };

    console.log('summaryItems from form custom', this.activityService?.summaryItems);

    // Open popup only if a matching logic exists
    console.log('!!relevantQuestionLogic', relevantQuestionLogic);
    // console.log('this.sharedDataService.isRestoring', this.sharedDataService.isRestoring);
    this.showActivityPopup = !!relevantQuestionLogic && !this.sharedDataService.isRestoring;
    console.log('showActivityPopup', this.showActivityPopup);
    if (this.showActivityPopup) {
      this.openedFromSummary = false;
      this.showActivitySummary = false;
      setTimeout(() => {
        cdRef.detectChanges();
      }, 0);
    }
  }

  onActivitySummaryClick() {
    console.log('activity summary clicked');
    this.showActivitySummary = true;
    this.showActivityPopup = true;
    this.showSignoffError = false;
  }

  // updateActivityTitlesFromSummary(formBuilderDetail: any) {
  //   this.activityService.getSummaryItems().forEach((summaryItem: any) => {
  //     const section = formBuilderDetail.sections.find(
  //       (s: any) => s.sectionId === summaryItem.sectionId
  //     );
  //     if (section) {
  //       const question = section.questions.find(
  //         (q: any) => q.questionId === summaryItem.questionId
  //       );
  //       if (question && question.questionLogics) {
  //         console.log('question.questionLogics', question.questionLogics);
  //       }

  //       question.questionLogics.forEach((logic: any) => {
  //         console.log('logic', logic);
  //         if (logic.isActTitlePredefined === false) {
  //           logic.activityTitle = summaryItem.title;
  //         }

  //         if (logic.isDescriptionPredefined === false) {
  //           logic.activityDescription = summaryItem.formValues.activityDescription;
  //         }

  //         if (logic.isExplanationPredefined === false) {
  //           logic.activityExplanation = summaryItem.formValues.activityExplanation;
  //         }

  //         logic.isHighPriorityActivity = summaryItem.formValues.isHighPriorityActivity;

  //         const ownerIds = summaryItem.formValues.owner || [];
  //         logic.activityOwnerID = ownerIds.length > 0 ? ownerIds[0] : null;

  //         const assigneeIds = summaryItem.formValues.assignees || [];
  //         logic.questionLogicAssignees.selectedUsersAndGroups = assigneeIds.map(
  //           (id: string) => ({
  //             userId: id,
  //             notifyType: 1
  //           })
  //         );
  //       });
  //     }
  //   });
  // }

  updateActivityTitlesFromSummary(formBuilderDetail: any) {
    this.activityService.getSummaryItems().forEach((summaryItem: any) => {
      const section = formBuilderDetail.sections.find(
        (s: any) => s.sectionId === summaryItem.sectionId
      );
      if (!section) return;

      const question = section.questions.find(
        (q: any) => q.questionId === summaryItem.questionId
      );
      if (!question || !question.questionLogics) return;

      // Only update the logic line this summary item was created for
      const summaryLogicId = summaryItem.popupData?.questionLogic?.questionLogicId;
      const matchingLogic = question.questionLogics.find(
        (logic: any) => logic.questionActionTypeID === 20 && logic.questionLogicId === summaryLogicId
      );

      if (!matchingLogic) return;

      console.log('Updating logic:', matchingLogic.questionLogicId);

      if (matchingLogic.isActTitlePredefined === false) {
        matchingLogic.activityTitle = summaryItem.title;
      }

      if (matchingLogic.isDescriptionPredefined === false) {
        matchingLogic.activityDescription = summaryItem.formValues.activityDescription;
      }

      if (matchingLogic.isExplanationPredefined === false) {
        matchingLogic.activityExplanation = summaryItem.formValues.activityExplanation;
      }

      if (matchingLogic.isDatePredefined === false) {
        // matchingLogic.dateValue = summaryItem.formValues.dueDate 
        //   ? summaryItem.formValues.dueDate.split('T')[0] 
        //   : null;
        if (summaryItem.formValues.dueDate) {
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);

          const dueDate = new Date(summaryItem.formValues.dueDate);
          dueDate.setHours(0, 0, 0, 0);

          const diffTime = dueDate.getTime() - currentDate.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          matchingLogic.activityRepeatDurationValue = diffDays > 0 ? diffDays : 1;
          matchingLogic.activityRepeatDurationType = 'Days';
          matchingLogic.dateValue = null;
        } else {
          matchingLogic.activityRepeatDurationValue = null;
          matchingLogic.activityRepeatDurationType = 'Days';
          matchingLogic.dateValue = null;
        }
      }

      matchingLogic.isHighPriorityActivity = summaryItem.formValues.isHighPriorityActivity;

      const ownerIds = summaryItem.formValues.owner || [];
      // OLD
      // matchingLogic.activityOwnerID = ownerIds.length > 0 ? ownerIds[0] : null;
      // NEW

      if (ownerIds.length > 0) {
        const ownerId = ownerIds[0];
        
        // Check if it's a number (entity role) or string (user GUID)
        if (typeof ownerId === 'number') {
          matchingLogic.activityEntityRoleOwnerID = ownerId;
          matchingLogic.activityOwnerID = '00000000-0000-0000-0000-000000000000';
          matchingLogic.activityOwnerIsUser = false;
          matchingLogic.activityOwnerIsEntityRole = true;
        } else {
          matchingLogic.activityOwnerID = ownerId;
          matchingLogic.activityEntityRoleOwnerID = 0;
          matchingLogic.activityOwnerIsUser = true;
          matchingLogic.activityOwnerIsEntityRole = false;
        }
      } else {
        matchingLogic.activityOwnerID = null;
        matchingLogic.activityEntityRoleOwnerID = 0;
      }

      const assigneeIds = summaryItem.formValues.assignees || [];
      const assigneesDisplay = summaryItem.formValues.assigneesDisplay || [];
      console.log('assigneeIds', assigneeIds);
      // matchingLogic.questionLogicAssignees.selectedUsersAndGroups = assigneeIds.map(
      //   (id: string) => ({
      //     userId: id,
      //     notifyType: 1
      //   })
      // );
      matchingLogic.questionLogicAssignees.selectedUsersAndGroups = assigneesDisplay
        .filter((assignee: any) => !assignee.isRole)
        .map((assignee: any) => {
          if (assignee.isGroup) {
            return {
              userId: null,
              groupId: assignee.groupId,
              notifyType: 2
            };
          } else {
            return {
              userId: assignee.userId,
              groupId: null,
              notifyType: 1
            };
          }
        });

      const roleAssignees = assigneesDisplay.filter((assignee: any) => assignee.isRole);
      if (roleAssignees.length > 0) {
        matchingLogic.questionLogicAssignees.selectedEntityRoles = roleAssignees.map((role: any) => ({
          entityRoleId: role.entityRoleId,
          entityRoleName: role.userName
        }));
      }

    });
  }
}